import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { createUserWithEmailAndPassword, signOut, updatePassword, deleteUser } from 'firebase/auth';
import { readGlobalProfiles } from '@services/firebaseGlobal';
import { getFirebase, getProdDb } from '@services/firebase';
import { doc, setDoc, serverTimestamp, getDoc, deleteDoc } from 'firebase/firestore';
import { FaTimes } from 'react-icons/fa';
import { FN_BASE } from '@services/fnBase';

const Signup = () => {
  const { auth, db } = getFirebase();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Decode URL params (set by AppShare invite links)
  const urlEmail = (() => { try { const em = searchParams.get('em'); return em ? atob(em) : ''; } catch { return ''; } })();
  const urlRole  = searchParams.get('role') || '';
  const urlEnv   = searchParams.get('env')  || '';
  const urlRef   = searchParams.get('ref')  || '';
  const urlExp   = searchParams.get('exp')  ? parseInt(searchParams.get('exp'), 10) : 0;
  const urlInvite = searchParams.get('inv') || '';
  const isLinkExpired = urlExp > 0 && Date.now() > urlExp;

  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    surname: '',
    email: urlEmail || location.state?.email || '',
  });

  const [birthYear, setBirthYear] = useState('');
  const [showYearPicker, setShowYearPicker] = useState(false);
  const yearPickerRef = useRef(null);
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 73 }, (_, i) => currentYear - 18 - i);
  const calculatedAge = birthYear ? currentYear - parseInt(birthYear) : '';

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (yearPickerRef.current && !yearPickerRef.current.contains(e.target)) {
        setShowYearPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Form validation
  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.surname.trim()) newErrors.surname = 'Surname is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Valid email is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const sendSecurityCode = async () => {
         
      try {
        const email = formData.email.trim().toLowerCase();
        const response = await fetch(`${FN_BASE}/sendSecurityCode`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email, invite: urlInvite }),
        });
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.error?.message);
        }
        return data;
      } catch (err) {
        setErrors({ general: `Failed to send security code: ${err.message}` });
      throw err;
      }
    };

  // Handle form submission with security code
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLinkExpired) {
      setErrors({ general: 'This invite link has expired. Please ask for a new one.' });
      return;
    }
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      // If this is an AppShare pre-approved invite, the email must match the URL param.
      // Guard against iOS autofill blanking or replacing the pre-filled value.
      if (urlEmail && formData.email.trim().toLowerCase() !== urlEmail.trim().toLowerCase()) {
        setErrors({ email: 'Email does not match the invite link. Please use the original link.' });
        return;
      }

      // Check if user already exists — if so, redirect to login without sending a code.
      // Login.jsx will send it once on email entry.
      const existingProfiles = await readGlobalProfiles('profile');
      const alreadyExists = existingProfiles.some(
        p => (p.email || '').trim().toLowerCase() === formData.email.trim().toLowerCase()
      );
      if (alreadyExists) {
        navigate('/login', { state: { email: formData.email.trim().toLowerCase(), autoSend: true } });
        return;
      }

      // Always call sendSecurityCode to get the Firebase auth password (loginPass).
      // For email-based invites (urlEmail present), store the embedded reference code in
      // sessionStorage in case Login.jsx wants to auto-fill it in the future.
      // For normal shared links (urlRef only, no urlEmail), urlRef is the referrer's ID
      // used for attribution — it is NOT the admin reference code needed at first login.
      if (urlRef && urlEmail) {
        try { sessionStorage.setItem('pending_invite_ref', urlRef); } catch (_) {}
      }

      // Create the Firebase auth account with a random temporary password.
      // sendSecurityCode (which sends the confirmation email) is called AFTER this
      // so the Firebase user + cUsers record exist before any email is dispatched.
      const tmpPass = Array.from(crypto.getRandomValues(new Uint8Array(16)))
        .map(b => b.toString(16).padStart(2, '0')).join('');
      const { user } = await createUserWithEmailAndPassword(auth, formData.email, tmpPass);

      // Determine role flags — use URL-pre-assigned role if present, default to plain User
      const assignedRole = urlRole || 'User';
      // Save to cUsers (before emailing so the account is fully initialised first)
      const userData = {
        isactive: false,
        isuser: assignedRole === 'User',
        isadminuser: assignedRole === 'Admin',
        issuperuser: assignedRole === 'Super',
        ismover: assignedRole === 'Mover',
        isbuyer: assignedRole === 'Buyer',
        last_login: new Date().toISOString(),
        first_login: true,
        isflag: false,
        userId: user.uid,
        env: urlEnv || 'Prod',
        created: serverTimestamp(),
      };
      await setDoc(doc(db, 'cUsers', user.uid), userData);

      // Call sendSecurityCode now that the Firebase user + cUsers record exist.
      // The profile doc is NOT yet in Firestore, so the CF treats this as a new-user
      // signup: stores a reference code in pendingSecurityCodes, emails the admin that
      // code, and sends the user a confirmation link. Returns the fixed loginPass.
      const { loginPass, preApproved } = await sendSecurityCode();

      // Replace the temporary auth password with the real loginPass used at login.
      await updatePassword(user, loginPass);

      // Verify cUsers write
      const userList = await readGlobalProfiles('cUsers');
      const userDoc = userList.find(doc => doc.id === user.uid);
      if (!userDoc) {
        console.error('Failed to verify cUsers document for UID:', user.uid);
        throw new Error('Failed to create cUsers document after write.');
      }
      
      // Save to profile..
      const profileData = {
        f_name: formData.firstName,
        l_name: formData.surname,
        email: formData.email,
        age: calculatedAge ? String(calculatedAge) : '',
        birth_day: birthYear ? `${birthYear}-01-01` : '',
        userId: user.uid,
      };
      await setDoc(doc(db, 'profile', user.uid), profileData);

      // Store security code for first login verification and navigate to success page
      await setDoc(doc(db, 'cUsers', user.uid), { isactive: false, first_login: true }, { merge: true });
      if (auth.currentUser) {
        await signOut(auth);
      }
      navigate('/signup-success', { replace: true, state: { emailPreApproved: preApproved === true } });
    } catch (error) {
      console.error('Signup failed:', error.message, error.stack);
      setErrors({ general: error.message || 'Signup failed. Please try again.' });
      if (error.code === 'auth/email-already-in-use') {
        // Email already exists — send them to login with email pre-filled
        navigate('/login', { state: { email: formData.email.trim().toLowerCase(), autoSend: true } });
        return;
      }
      // Roll back: delete the Firebase Auth account and any partial Firestore docs
      // so the dangling account cannot be used to log in.
      try {
        const partialUser = auth.currentUser;
        if (partialUser) {
          // Remove any cUsers / pendingSecurityCodes docs written before the failure
          try { await deleteDoc(doc(db, 'cUsers', partialUser.uid)); } catch (_) {}
          try { await deleteDoc(doc(db, 'profile', partialUser.uid)); } catch (_) {}
          try { await deleteDoc(doc(getProdDb(), 'pendingSecurityCodes', formData.email.trim().toLowerCase())); } catch (_) {}
          await deleteUser(partialUser);
        }
      } catch (deleteErr) {
        console.error('Error rolling back partial signup:', deleteErr.message, deleteErr.stack);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLinkExpired) {
    return (
      <div className="signup-container" style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏱️</div>
        <h2 style={{ color: '#fa4848', marginBottom: '8px' }}>Invite Link Expired</h2>
        <p style={{ color: '#666', marginBottom: '20px' }}>
          This signup link is no longer valid. Please ask for a new invite link.
        </p>
        <button onClick={() => navigate('/login')} style={{ background: '#fa4848', color: '#fff', border: 'none', borderRadius: '6px', padding: '10px 24px', cursor: 'pointer', fontWeight: '600' }}>
          Go to Login
        </button>
      </div>
    );
  }

  return (
    <div className="signup-container">
      <button className="close-btn" onClick={() => navigate('/login')}>
        <FaTimes />
      </button>

      <form onSubmit={handleSubmit}>
        <div className="signup-header-spacer" />
        <div className="form-grid">
          <div className="form-group">
            <label>First Name</label>
            <input
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              className="up-input-field"
            />
            {errors.firstName && <div className="error">{errors.firstName}</div>}
          </div>

          <div className="form-group">
            <label>Surname</label>
            <input
              type="text"
              name="surname"
              value={formData.surname}
              onChange={handleChange}
              className="up-input-field"
            />
            {errors.surname && <div className="error">{errors.surname}</div>}
          </div>

          <div className="form-group form-group-full">
            <label>
              Email Address
              {urlEmail && <span style={{ marginLeft: '6px', fontSize: '11px', color: '#22c55e', fontWeight: '600' }}>✓ Pre-filled from invite</span>}
              {urlRef && !urlEmail && <span style={{ marginLeft: '6px', fontSize: '11px', color: '#22c55e', fontWeight: '600' }}>✓ Referral link applied</span>}
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              readOnly={!!urlEmail}
              className="up-input-field"
              style={urlEmail ? { background: '#f0f9f0', color: '#333', cursor: 'not-allowed' } : {}}
            />
            {errors.email && <div className="error">{errors.email}</div>}
          </div>

          <div className="form-group">
            <label>Age</label>
            <div className="year-picker-wrapper" ref={yearPickerRef}>
              <input
                type="text"
                readOnly
                value={birthYear ? `${calculatedAge}` : ''}
                placeholder=""
                onClick={() => setShowYearPicker(v => !v)}
                className="up-input-field year-picker-input"
              />
              {showYearPicker && (
                <div className="year-picker-dropdown">
                  {years.map(year => (
                    <button
                      type="button"
                      key={year}
                      className={`year-option${birthYear === String(year) ? ' selected' : ''}`}
                      onClick={() => { setBirthYear(String(year)); setShowYearPicker(false); }}
                    >
                      {year}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {errors.general && <div className="error" style={{ textAlign: 'center' }}>{errors.general}</div>}

        <div className="terms-checkbox-item">
          <input
            type="checkbox"
            id="agreeTerms"
            checked={agreedToTerms}
            onChange={(e) => setAgreedToTerms(e.target.checked)}
          />
          <label htmlFor="agreeTerms">
            I agree to the{' '}
            <a href="https://www.mycreativeworld.app/privacy-policy.html" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
            {' '}and{' '}
            <a href="https://www.mycreativeworld.app/terms-of-service.html" target="_blank" rel="noopener noreferrer">Terms of Service</a>
          </label>
        </div>

        {(() => {
          const enabled = !isSubmitting && agreedToTerms && formData.firstName.trim() && formData.surname.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email);
          return (
            <button
              type="submit"
              disabled={!enabled}
            >
              {isSubmitting ? 'Signing Up...' : 'Sign Up'}
            </button>
          );
        })()}

        {isSubmitting && (
          <div className="loading-overlay">
            <div className="spinner"></div>
          </div>
        )}
      </form>

      <div className="login-link-wrapper">
        <a href="/login" className="login-link">Already a User? Sign In</a>
      </div>

      <style>{`
        .signup-container {
          max-width: 600px;
          margin: 2rem auto;
          padding: 2rem;
          background: white;
          border-radius: 8px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.1);
          position: relative;
          font-size: 1.1rem;
        }
        .close-btn {
          position: absolute;
          top: 1rem;
          right: 1rem;
          background: none;
          border: none;
          cursor: pointer;
          color: #666;
          font-size: 1.3rem;
          z-index: 1;
        }
        .signup-header-spacer {
          height: 2rem;
        }
        .form-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1rem;
          margin-bottom: 1.5rem;
        }
        .form-group {
          margin-bottom: 1rem;
        }
        .form-group-full {
          grid-column: 1 / -1;
          margin-bottom: 1.5rem;
        }
        .year-picker-wrapper {
          position: relative;
        }
        .year-picker-input {
          cursor: pointer;
        }
        .year-picker-dropdown {
          position: absolute;
          top: calc(100% + 4px);
          left: 0;
          right: 0;
          background: white;
          border: 1px solid #ddd;
          border-radius: 6px;
          z-index: 200;
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 2px;
          padding: 6px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.12);
        }
        .year-option {
          background: none;
          border: none;
          border-radius: 4px;
          padding: 5px 2px;
          font-size: 0.85rem;
          cursor: pointer;
          color: #333;
          text-align: center;
          width: 100%;
          margin: 0;
        }
        .year-option:hover {
          background: #f5f5f5;
        }
        .year-option.selected {
          background: #fa4848;
          color: white;
        }
        html[data-theme="dark"] .year-picker-dropdown {
          background: #2a2a2a;
          border-color: #444;
        }
        html[data-theme="dark"] .year-option {
          color: #eee;
        }
        html[data-theme="dark"] .year-option:hover {
          background: #3a3a3a;
        }
        .signup-container label {
          display: block;
          margin-bottom: 0.3rem;
          color: #555;
          font-size: 1.15rem;
        }
        .signup-container .up-input-field,
        .signup-container .up-select-field {
          width: 100%;
          padding: 0.6rem 0;
          border: none !important;
          border-bottom: 1px solid #ddd !important;
          font-size: 1.15rem !important;
          background: transparent;
          color: #333;
        }
        .signup-container .terms-checkbox-item {
          display: flex;
          align-items: flex-start;
          gap: 0.6rem;
          margin-top: 1.2rem;
          font-size: 0.65rem !important;
          color: #555;
        }
        .signup-container .terms-checkbox-item input {
          width: auto;
          margin: 0.2rem 0 0;
          cursor: pointer;
          flex-shrink: 0;
        }
        .signup-container .terms-checkbox-item label {
          display: inline;
          margin: 0;
          cursor: pointer;
          line-height: 1.5;
          font-size: 0.65rem !important;
        }
        .signup-container .terms-checkbox-item a {
          color: #fa4848;
          text-decoration: underline;
        }
        .signup-container button[type="submit"] {
          width: 100%;
          padding: 0.85rem;
          background: #fa4848;
          color: white;
          border: none;
          border-radius: 4px;
          font-size: 0.85rem !important;
          cursor: pointer;
          margin-top: 1.5rem;
        }
        .signup-container button[type="submit"]:disabled {
          background: #cccccc !important;
          color: #fff !important;
          cursor: not-allowed;
        }
        html[data-theme="dark"] .signup-container button[type="submit"]:disabled {
          background: #555 !important;
          color: #999 !important;
        }
        .login-link-wrapper {
          text-align: center;
          margin-top: 1.2rem;
        }
        .login-link {
          color: #fa4848;
          font-size: 1rem;
          text-decoration: none;
        }
        .error {
          color: #fa4848;
          font-size: 0.95rem;
          margin-top: 0.25rem;
        }
        .loading {
          text-align: center;
          padding: 2rem;
          font-size: 1.1rem;
        }
        .loading-overlay {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(255, 255, 255, 0.8);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 100;
        }
        .spinner {
          border: 4px solid rgba(0, 0, 0, 0.1);
          border-left-color: #fa4848;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @media (max-width: 600px) {
          .form-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default Signup;